import { forwardRef } from "react";
import { CashIcon, SwitchHorizontalIcon, XCircleIcon } from "icons/outline";
import { cn, formatCurrency, formatNumber, Skeleton } from "ui";
import { useDatadogQuery } from "./DatadogAnalytics/useDatadogQuery";

type MetricProps = {
  Icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  label: string;
  count: number | string;
  /** Background color of the icon square. Defaults to `bg-foundations-subtler`. */
  iconClassName?: string;
};

type MetricValue = {
  value: number;
  diff_percentage: number;
};

type TotalMetricsData = {
  transactions: MetricValue;
  revenue: MetricValue;
  declines: MetricValue;
};

const TOTAL_METRICS: {
  key: keyof TotalMetricsData;
  Icon: MetricProps["Icon"];
  label: string;
  iconClassName: string;
  format: (value: number) => string;
}[] = [
  {
    key: "transactions",
    Icon: SwitchHorizontalIcon,
    label: "Total transactions",
    iconClassName: "bg-purple-100",
    format: (value) => formatNumber({ value, compact: true }),
  },
  {
    key: "revenue",
    Icon: CashIcon,
    label: "Total revenue",
    iconClassName: "bg-warning-subtler",
    format: (value) =>
      formatCurrency({ amount: value, currency: "USD", compact: true }),
  },
  {
    key: "declines",
    Icon: XCircleIcon,
    label: "Total declines",
    iconClassName: "bg-info-subtler",
    format: (value) => formatNumber({ value, compact: true }),
  },
];

export function TotalMetrics() {
  const { ref, queryStatus, data } = useDatadogQuery<TotalMetricsData>({
    chartType: "TOTAL_METRICS",
  });

  if (queryStatus === "loading") return <TotalMetricsSkeleton ref={ref} />;

  return (
    <div ref={ref} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {TOTAL_METRICS.map(({ key, Icon, label, iconClassName, format }) => (
        <Metric
          key={label}
          Icon={Icon}
          label={label}
          iconClassName={iconClassName}
          count={format(data?.[key]?.value ?? 0)}
        />
      ))}
    </div>
  );
}

export const Metric = ({
  Icon,
  label,
  count,
  iconClassName = "bg-foundations-subtler",
}: MetricProps) => (
  <div className="flex flex-1 items-center justify-between gap-2 rounded-lg border border-foundations-subtle bg-foundations-default p-4 text-foundations-boldest">
    <div className="flex items-center gap-2">
      <div
        className={cn(
          "flex size-9 items-center justify-center rounded-md",
          iconClassName
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-sm font-medium">{label}</p>
    </div>

    <span className="text-lg font-bold">{count}</span>
  </div>
);

export const TotalMetricsSkeleton = forwardRef<HTMLDivElement>(
  function TotalMetricsSkeleton(_props, ref) {
    return (
      <div ref={ref} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {Array.from({ length: 3 }, (_, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between gap-2 rounded-lg border border-foundations-subtle bg-foundations-default p-4"
          >
            <div className="flex items-center gap-2">
              <Skeleton className="size-9 rounded-md" />
              <Skeleton className="h-4 w-28" />
            </div>
            <Skeleton className="h-5 w-10" />
          </div>
        ))}
      </div>
    );
  }
);
